import { Controller } from "@hotwired/stimulus";
import { moneyString } from "../../shared/money_format";

/**
 * The one thing that writes the builder's total
 * (CAL-499).
 *
 * `total = max(0, goods - discount) + fee`, printed into
 * the payment card's block and the sticky bar at once,
 * with the label and the fee row always agreeing with it
 * (contract R17).
 *
 * ONE WRITER, DELIBERATELY. `tip-selector` owns every
 * total on the customer checkout and used to own these
 * two as well, which was fine until the discount had to
 * move them. Subtracting "over" its answer cannot be
 * made idempotent: reading the number back out of the
 * DOM double-subtracts the moment anything runs twice,
 * and computing it independently is a second answer, not
 * a subtraction. Either way both controllers write the
 * same nodes and correctness rests on which window
 * listener registered first. That is not a coin a
 * merchant's read-aloud total should ride on, so
 * `tip-selector` is off the builder entirely: it has no
 * tip UI to run here and nothing else it did is needed.
 *
 * WHAT MOVES THE NUMBER, in the three ways it can move:
 *
 * - a courier quote lands or is refused, which is the
 *   only way a courier fee ever exists (the server
 *   renders none, because it has not priced the trip)
 * - the merchant types in the discount boxes, which used
 *   to leave the Total unchanged until they pressed
 *   Create order, so they read the undiscounted number
 *   down the phone
 * - the basket changes, which arrives as
 *   `manual-order-basket` writing a new `goodsCents`
 *   onto this element from inside the frame the stream
 *   replaced
 *
 * THE SCREEN MUST NOT PROMISE MORE THAN THE ORDER WILL
 * CARRY. `StaffDiscountable#staff_discount_amount` caps
 * the discount at what is left of the goods, ROUNDS a
 * percent to the nearest cent, and touches neither the
 * delivery fee nor the tip. Every one of those is
 * mirrored below, against the same base, because a screen
 * offering a bigger discount than the order will take is
 * worse than one that does not move at all.
 *
 * AND THE BASE IS NOT THE BASKET. A matched customer's
 * loyalty tier is written into the order BEFORE the
 * merchant's discount, so the discount is a percent of
 * what the tier left. The tier's rule arrives on the page
 * root and is applied here (`tierGoodsCents`); it is a
 * page-lifetime constant, because a customer is attached
 * only by "Use", which is a full page GET. Reading the
 * basket as the base is what had this screen promising
 * $306.00 on an order of $275.40 (CAL-499).
 *
 * A HALF-TYPED VALUE IS NOT A DISCOUNT. Anything that
 * does not parse, a zero, a negative, a percent over a
 * hundred, or both boxes filled at once (which the form
 * refuses) all show the UNDISCOUNTED total rather than a
 * guess. A merchant mid-keystroke must never watch a
 * wrong number flash.
 */
/**
 * `numerator / denominator` rounded DOWN, computed
 * without trusting the float quotient.
 *
 * `Math.floor(a / b)` is exact for every input this file
 * can produce, and the two corrections below are still
 * here rather than left implicit. The float quotient can
 * only land on the wrong side of an integer when the true
 * quotient is nearer to it than half an ulp, which needs
 * a numerator past `Number.MAX_SAFE_INTEGER`; the
 * numerators here are cents times a percent's units, so a
 * ten million peso basket at three decimals is still four
 * orders of magnitude short. The bound is real, but it is
 * a bound nothing states, and the day someone prices
 * something other than cents with this it stops holding
 * silently. Two comparisons make the function correct on
 * its whole domain instead of on an unwritten part of it.
 */
function flooredQuotient(numerator, denominator) {
  let whole = Math.floor(numerator / denominator);

  if (whole * denominator > numerator) whole -= 1;
  else if ((whole + 1) * denominator <= numerator) whole += 1;

  return whole;
}

/**
 * `numerator / denominator` to the nearest integer, with
 * a tie going UP, computed without ever forming the
 * quotient as a float.
 *
 * That is Ruby's `.round` on a non-negative BigDecimal,
 * which is what the server does to the STAFF discount.
 * Both arguments are non-negative integers here, so
 * `remainder * 2 >= denominator` is the tie test and it
 * is exact.
 */
function roundedQuotient(numerator, denominator) {
  const whole = flooredQuotient(numerator, denominator);
  const remainder = numerator - whole * denominator;

  return remainder * 2 >= denominator ? whole + 1 : whole;
}

/**
 * A decimal string as an EXACT fraction, `[units, scale]`,
 * meaning `units / scale`.
 *
 * The scale is read off the STRING, which is the same
 * thing Ruby's `to_d` reads, so "9.2" is 92/10 rather
 * than the 9.199999999999999 a double holds.
 *
 * Two callers, and they must not drift: the percent the
 * merchant is typing, and the tier percentage the page
 * root publishes. Both are priced against the same basket
 * one after the other, and a percent parsed two ways is
 * two answers to one question.
 */
function fractionOf(raw) {
  const text = (raw || "").trim();
  if (text === "") return [0, 1];

  const decimals = (text.split(".")[1] || "").length;
  const scale = 10 ** decimals;
  const units = Math.round(Number(text) * scale);

  return [Number.isFinite(units) ? units : 0, scale];
}

export default class extends Controller {
  static targets = [
    "subtotal",
    "promotionsRow",
    "promotions",
    "tierRow",
    "tierAmount",
    "feeRow",
    "fee",
    "discountRow",
    "discountAmount",
    "discountReason",
    "label",
    "total",
    "amountInput",
    "percentInput",
    "reasonInput",
  ];
  static values = {
    subtotalCents: Number,
    promotionsCents: Number,
    goodsCents: Number,
    feeCents: Number,
    hasFee: Boolean,
    // THE TIER'S RULE, not what it is worth. The amount
    // moves with every line the merchant adds; the rule
    // cannot move at all, because a customer is attached
    // only by "Use", which is a full page GET. Publishing
    // the amount instead would go stale on exactly the
    // requests that carry no `user_id`, the line's own
    // form, and the Total would jump back UP.
    tierPercent: {type: String, default: ""},
    tierFreeDelivery: Boolean,
    total: String,
    totalWithDelivery: String,
    currencySymbol: {type: String, default: "$"},
    currencyDelimiter: {type: String, default: ","},
    currencySeparator: {type: String, default: "."},
  };

  connect() {
    // Set AFTER Stimulus has delivered the initial value
    // callbacks, which it does before `connect()`. The
    // server has just rendered every one of these rows
    // correctly, and rewriting them on arrival would only
    // be a chance to disagree with it.
    this.started = true;

    this.onQuotePriced = this.handleQuotePriced.bind(this);
    window.addEventListener(
      "courier-quote:priced",
      this.onQuotePriced
    );
  }

  disconnect() {
    window.removeEventListener(
      "courier-quote:priced",
      this.onQuotePriced
    );
  }

  /**
   * The basket moved. Fired by Stimulus when
   * `manual-order-basket` writes the new figures from
   * inside the frame the stream replaced, which is the
   * only signal this controller gets that a line was
   * added, removed or changed.
   *
   * ALL THREE, not just the goods figure. A mutation can
   * move the subtotal while leaving the goods identical:
   * an item added that triggers a promotion worth exactly
   * what it cost nets to the same total, and the two rows
   * above it are still wrong. Keying only on the number
   * this controller happens to compute FROM would have
   * left those stale.
   *
   * The renders are idempotent, so three writes landing
   * as three callbacks cost three identical repaints and
   * settle in the same place.
   */
  subtotalCentsValueChanged() {
    this.renderAfterStart();
  }

  promotionsCentsValueChanged() {
    this.renderAfterStart();
  }

  goodsCentsValueChanged() {
    this.renderAfterStart();
  }

  renderAfterStart() {
    if (!this.started) return;

    this.render();
  }

  /**
   * `feeCents` null is every state that is not a price:
   * no address yet, an address we cannot deliver to, a
   * quote that expired, a request that never came back.
   * All of them mean the same thing to the number and to
   * the words around it, which is why they are not told
   * apart here.
   */
  handleQuotePriced(event) {
    const {feeCents} = event.detail || {};
    const priced = feeCents !== null && feeCents !== undefined;

    this.hasFeeValue = priced;
    this.feeCentsValue = priced ? Number(feeCents) : 0;
    this.render();
  }

  recompute() {
    this.render();
  }

  /**
   * THE CASCADE, in the order the operation writes it:
   * the basket's own promotions are already off
   * `goodsCents`, then the tier perk, then the merchant's
   * own discount against what THAT left.
   *
   * The middle step is the whole point.
   * `StaffDiscountable#staff_discount_base` subtracts
   * every stage above it, the tier percentage among
   * them, so a screen taking the
   * merchant's percent off the untiered basket promised a
   * matched customer more than the order would take: on a
   * $340 basket at 10% and 10% it read $306.00 against an
   * order of $275.40, and under "Already paid" the
   * merchant collected the first figure.
   *
   * The waived delivery is NOT in that base. It shrinks
   * the FEE, is written `applies_to: "delivery"`, and
   * `reducing_subtotal` excludes it for exactly that
   * reason: a merchant comping the food is not comping the
   * trip, and a free trip is not more food to comp.
   */
  render() {
    const goods = Math.max(0, this.goodsCentsValue);
    const fee = this.hasFeeValue ? this.feeCentsValue : 0;

    const tierGoods = this.tierGoodsCents(goods);
    const tierDelivery =
      this.tierFreeDeliveryValue ? fee : 0;

    const discount = this.discountCents(goods - tierGoods);
    const owed = Math.max(0, goods - tierGoods - discount);

    this.write(
      owed + fee - tierDelivery,
      discount,
      tierGoods + tierDelivery
    );
  }

  /**
   * What the tier takes off the GOODS, capped there.
   *
   * TRUNCATED, not rounded, because
   * `Loyalty::TierPerks#discount_on` truncates (`to_i`)
   * where the staff discount below it rounds. The two
   * genuinely differ by up to a cent and that is worth
   * settling, but the screen's job is to say what the
   * order will say, and a money rule improved on one
   * surface is how these two came apart in the first
   * place.
   *
   * An empty percentage is a tier that grants no
   * percentage: one that only waives delivery still takes
   * nothing off the food.
   */
  tierGoodsCents(goods) {
    const [units, scale] = fractionOf(this.tierPercentValue);
    if (units <= 0) return 0;

    return Math.min(
      goods,
      flooredQuotient(goods * units, 100 * scale)
    );
  }

  write(totalCents, discountCents, tierCents) {
    // The cart's own rows, which come from the basket
    // figure rather than from anything the right column
    // carries. Written here so that a line mutation whose
    // response CANNOT price the money frames still leaves
    // every number on this block agreeing with every
    // other one: a subtotal of $0.00 over a total of
    // $95.00 is three numbers that do not reconcile,
    // which is the same objection that gave promotions a
    // row of their own.
    this.subtotalTargets.forEach((el) => {
      el.textContent = this.money(this.subtotalCentsValue);
    });
    this.promotionsRowTargets.forEach((row) => {
      row.classList.toggle(
        "hidden",
        this.promotionsCentsValue <= 0
      );
    });
    this.promotionsTargets.forEach((el) => {
      el.textContent = this.money(
        -this.promotionsCentsValue
      );
    });

    // The tier's own row, between the promotion above it
    // and the merchant's discount below, because that is
    // where the cascade puts it. Both perks in one figure,
    // the way `RestaurantOrder#loyalty_tier_discount` sums
    // them. The words beside it are the SERVER's and are
    // never rewritten here: which perks a customer holds
    // cannot change while the page is open, only what they
    // are worth.
    this.tierRowTargets.forEach((row) => {
      row.classList.toggle("hidden", tierCents <= 0);
    });
    this.tierAmountTargets.forEach((el) => {
      el.textContent = this.money(-tierCents);
    });

    const label = this.hasFeeValue
      ? this.totalWithDeliveryValue
      : this.totalValue;

    this.labelTargets.forEach((el) => {
      el.textContent = label;
    });
    this.totalTargets.forEach((el) => {
      el.textContent = this.money(totalCents);
    });

    this.feeRowTargets.forEach((row) => {
      row.classList.toggle("hidden", !this.hasFeeValue);
    });
    this.feeTargets.forEach((el) => {
      el.textContent = this.money(this.feeCentsValue);
    });

    this.discountRowTargets.forEach((row) => {
      row.classList.toggle("hidden", discountCents <= 0);
    });
    this.discountAmountTargets.forEach((el) => {
      el.textContent = this.money(-discountCents);
    });

    // Kept in step with the amount beside it. A live
    // figure next to a blank where the reason should be
    // reads as a reason that did not save.
    this.discountReasonTargets.forEach((el) => {
      el.textContent = this.typedReason();
    });
  }

  typedReason() {
    if (!this.hasReasonInputTarget) return "";

    return (this.reasonInputTarget.value || "").trim();
  }

  /**
   * What the merchant's discount comes to against THIS
   * basket, by the same rules the server applies:
   * exactly one of the two sizes, a percent of what is
   * left after the basket's own promotions ROUNDED to
   * the nearest cent, and never more than there is to
   * discount.
   *
   * IN EXACT INTEGERS, and that is not a flourish. Ruby
   * prices this in BigDecimal, where a half cent is a
   * real half cent, and `.round` takes it up. A double
   * cannot hold 9.2, so the obvious
   * `Math.round((cents * percent) / 100)` computes
   * 34.49999999999999 for $3.75 at 9.2% and answers 34
   * where Ruby answers 35. Measured across 400,000
   * exact half cent inputs, that form disagreed on 9,032
   * of them: not a corner, a bias.
   *
   * `roundedQuotient` takes the same two integers Ruby's
   * BigDecimal does and never divides in floating point
   * at all, so there is nothing left to disagree about.
   *
   * `Math.round` breaks a tie toward positive infinity
   * and Ruby's `.round` breaks it away from zero. Those
   * differ only on negatives, and this magnitude is
   * never negative: the base is floored at zero here
   * exactly as `staff_discount_base` floors it there,
   * and the minus sign is painted on at render rather
   * than carried through the arithmetic. Keep it that
   * way.
   */
  discountCents(base) {
    const amount = this.parsedAmount();
    const percent = this.parsedPercent();

    // The form refuses both at once, so the screen says
    // nothing rather than picking one.
    if (amount !== null && percent !== null) return 0;

    // WHAT THE STAGES ABOVE LEFT, passed in rather than
    // read off `goodsCents` here. A percent of the raw
    // basket is a bigger discount than the order will
    // take, and the cap has to move with it or a comp of
    // "everything" comps the tier perk twice.
    const goods = Math.max(0, base);

    let typed = 0;
    if (percent !== null) {
      const [units, scale] = fractionOf(
        this.percentInputTarget.value
      );
      typed = roundedQuotient(goods * units, 100 * scale);
    } else if (amount !== null) {
      typed = amount;
    }

    return Math.min(Math.max(0, typed), goods);
  }

  /**
   * Pesos as typed, read the way `money_fields` reads
   * them: everything but digits, a dot and a sign
   * stripped, so the `$` and the group marks
   * `currency-input` paints in are ignored rather than
   * parsed.
   */
  parsedAmount() {
    if (!this.hasAmountInputTarget) return null;

    const raw = (this.amountInputTarget.value || "")
      .replace(/[^0-9.\-]/g, "");
    if (raw === "") return null;

    const units = Number(raw);
    if (!Number.isFinite(units) || units <= 0) return null;

    return Math.round(units * 100);
  }

  parsedPercent() {
    if (!this.hasPercentInputTarget) return null;

    const raw = (this.percentInputTarget.value || "").trim();
    if (raw === "") return null;

    const percent = Number(raw);
    if (!Number.isFinite(percent)) return null;
    if (percent <= 0 || percent > 100) return null;

    return percent;
  }

  money(cents) {
    return moneyString(cents, {
      symbol: this.currencySymbolValue,
      delimiter: this.currencyDelimiterValue,
      separator: this.currencySeparatorValue,
    });
  }
}
